/**
 * RFC 5545, the parts a phone's calendar actually enforces. Shared rather than written twice
 * because the same schedule leaves by two doors — a file she downloads (SCH-03) and a feed her
 * calendar subscribes to (SCH-04) — and two builders would eventually disagree about the same
 * four days.
 */

export interface IcsSession {
  id: string;
  title: string;
  /** ISO 8601 instants. Converted to UTC on the way out, so no timezone travels with them. */
  startsAt: string;
  endsAt: string;
  roomName?: string | null;
  roomLevel?: string | null;
  description?: string | null;
  isCancelled?: boolean;
  trackName?: string | null;
  /** Where to read more. Absolute, because a calendar entry is opened away from the app. */
  url?: string | null;
}

export interface IcsOptions {
  /** The event, used to label a track-less session and to finish an address. */
  eventName: string;
  venueName?: string | null;
  /**
   * What a calendar app calls the subscription in its sidebar. Set for a feed; leave unset for a
   * download, where the filename is the label and a calendar name would name the wrong thing.
   */
  calendarName?: string;
  /**
   * How long a subscribing client may wait before asking again. PRG-08 says a room that moved
   * half an hour ago is the point of the feed, so an hour is the ceiling rather than a default.
   */
  refreshHours?: number;
}

export function buildIcs(sessions: readonly IcsSession[], options: IcsOptions): string {
  const stamp = icsInstant(new Date().toISOString());

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ApolloRise//GFTG Companion//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...(options.calendarName
      ? [
          // NAME is RFC 7986; X-WR-CALNAME is what everything older reads. Both, or the
          // subscription shows up in her sidebar as the URL it came from.
          `NAME:${escapeText(options.calendarName)}`,
          `X-WR-CALNAME:${escapeText(options.calendarName)}`,
        ]
      : []),
    ...(options.refreshHours
      ? [
          `REFRESH-INTERVAL;VALUE=DURATION:PT${options.refreshHours}H`,
          `X-PUBLISHED-TTL:PT${options.refreshHours}H`,
        ]
      : []),
    ...sessions.flatMap((session) => vevent(session, options, stamp)),
    'END:VCALENDAR',
  ];

  // CRLF and 75-octet folding: the two parts of the spec that older desktop clients enforce.
  return lines.flatMap(fold).join('\r\n');
}

function vevent(session: IcsSession, options: IcsOptions, stamp: string): string[] {
  const location = [session.roomName, session.roomLevel, options.venueName]
    .filter(Boolean)
    .join(', ');

  return [
    'BEGIN:VEVENT',
    `UID:${session.id}@goforthegreens.org`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${icsInstant(session.startsAt)}`,
    `DTEND:${icsInstant(session.endsAt)}`,
    `SUMMARY:${escapeText(session.title)}`,
    ...(location ? [`LOCATION:${escapeText(location)}`] : []),
    ...(session.description ? [`DESCRIPTION:${escapeText(session.description)}`] : []),
    ...(session.url ? [`URL:${escapeText(session.url)}`] : []),
    // A cancelled session stays in the calendar, marked as what it is: deleting it would leave her
    // walking to a room at the old time because her phone forgot to mention it.
    session.isCancelled ? 'STATUS:CANCELLED' : 'STATUS:CONFIRMED',
    `CATEGORIES:${escapeText(session.trackName ?? options.eventName)}`,
    'END:VEVENT',
  ];
}

/** UTC instants, the one form no calendar app can misread. */
function icsInstant(iso: string): string {
  return new Date(iso)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

function fold(line: string): string[] {
  if (line.length <= 75) {
    return [line];
  }

  const parts = [line.slice(0, 75)];
  for (let index = 75; index < line.length; index += 74) {
    parts.push(` ${line.slice(index, index + 74)}`);
  }
  return parts;
}

/** A filename for a download. Not used by the feed, which is named by `calendarName`. */
export function icsFilename(title: string): string {
  const stem = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
  return `${stem || 'session'}.ics`;
}
